import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, X, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_COLORS = {
  new:                  'bg-slate-100 text-slate-600',
  test_pending:         'bg-blue-100 text-blue-700',
  test_completed:       'bg-purple-100 text-purple-700',
  shortlisted:          'bg-green-100 text-green-700',
  interview_scheduled:  'bg-teal-100 text-teal-700',
  rejected:             'bg-red-100 text-red-700',
  on_hold:              'bg-amber-100 text-amber-700',
};

const STATUS_LABELS = {
  new: 'New', test_pending: 'Test Pending', test_completed: 'Completed',
  shortlisted: 'Shortlisted', interview_scheduled: 'Interview Scheduled',
  rejected: 'Rejected', on_hold: 'On Hold',
};

export default function AdminAssessmentCandidates() {
  const [candidates, setCandidates] = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);

  const [search, setSearch]   = useState('');
  const [dept,   setDept]     = useState('');
  const [status, setStatus]   = useState('');

  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (search) params.set('search', search);
      if (dept)   params.set('department', dept);
      if (status) params.set('status', status);
      const res = await axios.get(`${API}/api/admin/assessment/candidates?${params}`);
      setCandidates(res.data.candidates);
      setTotal(res.data.total);
    } catch {
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, dept, status]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (cid, newStatus) => {
    try {
      await axios.patch(`${API}/api/admin/assessment/candidates/${cid}/status`, { status: newStatus });
      load();
    } catch { /* ignore */ }
  };

  const [deleting, setDeleting] = useState(null); // candidate object pending confirm

  const [deleteError, setDeleteError] = useState('');

  const deleteCandidate = async () => {
    if (!deleting) return;
    setDeleteError('');
    try {
      await axios.delete(`${API}/api/admin/assessment/candidates/${deleting.id}`);
      setDeleting(null);
      // if this was the last row on a page > 1, step back a page
      if (candidates.length === 1 && page > 1) setPage(p => p - 1);
      else load();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Delete failed. Please try again.');
    }
  };

  const pages = Math.ceil(total / LIMIT);

  const scoreColor = pct =>
    pct >= 75 ? 'text-green-600 font-bold' :
    pct >= 50 ? 'text-amber-600 font-semibold' :
                'text-red-500 font-semibold';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Candidates</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} total registered</p>
        </div>
        <Link to="/portal/admin/assessment"
          className="text-sm text-[#1e73be] hover:underline flex items-center gap-1">
          ← Dashboard
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search name or phone…"
            className="w-full h-10 pl-9 pr-8 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1e73be]/30" />
          {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
        </div>
        <select value={dept} onChange={e => { setDept(e.target.value); setPage(1); }}
          className="h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white outline-none">
          <option value="">All Departments</option>
          {['sales','telesales','hr','operations','other'].map(d => (
            <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
          ))}
        </select>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
          className="h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white outline-none">
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-slate-400">Loading…</div>
        ) : candidates.length === 0 ? (
          <div className="py-12 text-center text-slate-400">No candidates found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Name','Phone','Department','Applied For','Score','Status','Action'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {candidates.map(c => (
                  <tr key={c.id} className="hover:bg-slate-50 transition">
                    <td className="px-4 py-3 font-medium text-slate-800">
                      <Link to={`/portal/admin/assessment/candidates/${c.id}`}
                        className="hover:text-[#1e73be] hover:underline">
                        {c.full_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.phone}</td>
                    <td className="px-4 py-3 text-slate-600 capitalize">{c.department || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[150px] truncate">{c.designation_applied || '—'}</td>
                    <td className="px-4 py-3">
                      {c.overall_pct != null
                        ? <span className={scoreColor(c.overall_pct)}>{parseFloat(c.overall_pct).toFixed(1)}%</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLORS[c.status] || 'bg-slate-100 text-slate-600'}`}>
                        {STATUS_LABELS[c.status] || c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <select
                          value={c.status}
                          onChange={e => updateStatus(c.id, e.target.value)}
                          className="text-xs border border-slate-200 rounded px-2 py-1 bg-white outline-none">
                          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <button onClick={() => setDeleting(c)} title="Delete candidate (allow retest)"
                          className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Showing {(page-1)*LIMIT+1}–{Math.min(page*LIMIT,total)} of {total}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
              className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 disabled:opacity-40">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="flex items-center px-3 text-sm text-slate-600">{page} / {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p+1))} disabled={page === pages}
              className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 disabled:opacity-40">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDeleting(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6"
            onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-1">Delete Candidate?</h3>
            <p className="text-sm text-slate-600 mb-1">
              <strong>{deleting.full_name}</strong> ({deleting.phone})
            </p>
            <p className="text-sm text-slate-500 mb-5">
              This will permanently remove the candidate along with their test attempt and all answers.
              They will be able to register and take the test again with the same mobile number.
            </p>
            {deleteError && (
              <div className="mb-4 p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">{deleteError}</div>
            )}
            <div className="flex gap-3">
              <button onClick={() => { setDeleting(null); setDeleteError(''); }}
                className="flex-1 h-10 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 transition">
                Cancel
              </button>
              <button onClick={deleteCandidate}
                className="flex-1 h-10 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition">
                Delete & Allow Retest
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
